import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuthActions,
  AuthActionsContext,
  AuthContext,
  AuthState,
  AuthUser,
  CadastroTotp,
  ResultadoLogin,
  UserProfile,
} from '../contexts/AuthContext';
import {
  CognitoClient,
  SignInResult,
  isMfaChallenge,
  isNovaSenhaChallenge,
} from '../services/cognitoClient';
import { CognitoIdentityClient } from '../services/cognitoIdentityClient';
import { cadastroMfaPendente, expiraEmMs, tokenParaApi } from '../services/authToken';
import { limparMarcaMfa, marcarMfaVerificado, mfaVerificadoNaAba } from '../services/sessaoAba';
import { ApiClient } from '../services/apiClient';
import { MeApi } from '../services/meApi';
import { CognitoRuntimeConfig } from '../config/runtime-config';

/**
 * AuthProvider — provê estado + ações de autenticação à árvore.
 *
 * Integra o fluxo Cognito real (login SRP + MFA TOTP) via CognitoClient e resolve o
 * perfil do usuário via `GET /me` (M1) logo após o login (RF-M11-013). O 401 em
 * qualquer chamada dispara logout (RN-M1-002) — o apiClient chama `onUnauthorized`.
 *
 * ADR-031: o SDK guarda os tokens na `sessionStorage` da aba. No boot, a sessão da aba
 * é restaurada (`refresh()` do cliente + `GET /me`); alguns minutos antes de o id token
 * vencer, a sessão é renovada pelo refresh token (`refresh(true)`); sem refresh válido,
 * logout. A marca de MFA verificado vai junto na aba e só o desafio TOTP a grava.
 *
 * Para testes/dev, `sessaoInicial` injeta uma sessão pronta e `cognitoClient`
 * pode ser dublado; em produção usa o CognitoIdentityClient e a API reais.
 */
export interface AuthProviderProps {
  readonly children: ReactNode;
  /** URL base da API (para resolver `GET /me`). Obrigatória em produção. */
  readonly apiBaseUrl?: string;
  /** Config do Cognito (para o cliente real). Ausente em testes com dublê. */
  readonly cognitoConfig?: CognitoRuntimeConfig;
  /** Cliente Cognito (dublável em testes). Default: CognitoIdentityClient. */
  readonly cognitoClient?: CognitoClient;
  /** Resolve o perfil via GET /me (dublável em testes). */
  readonly resolveProfile?: (token: string) => Promise<AuthUser>;
  /** Sessão inicial pronta (dev/testes). */
  readonly sessaoInicial?: {
    readonly sub: string;
    readonly email?: string;
    readonly profiles: readonly UserProfile[];
    readonly token: string;
    readonly mfaVerified?: boolean;
  } | null;
}

/** Renova a sessão este tanto antes de o id token vencer (ADR-031). */
export const MARGEM_RENOVACAO_MS = 5 * 60 * 1000;
/** Espera mínima até a renovação (token já perto de vencer ao restaurar). */
const ESPERA_MINIMA_MS = 10 * 1000;

function estadoInicial(sessao: AuthProviderProps['sessaoInicial'], restaurando: boolean): AuthState {
  if (!sessao) {
    // Com cliente Cognito, começa carregando: o boot tenta restaurar a sessão da aba.
    return { user: null, loading: restaurando, token: undefined };
  }
  return {
    user: {
      sub: sessao.sub,
      email: sessao.email ?? null,
      profiles: sessao.profiles,
      mfaVerified: sessao.mfaVerified ?? false,
    },
    loading: false,
    token: sessao.token,
  };
}

export function AuthProvider({
  children,
  apiBaseUrl,
  cognitoConfig,
  cognitoClient,
  resolveProfile,
  sessaoInicial = null,
}: AuthProviderProps): JSX.Element {
  // Restaura a sessão da aba no boot quando há cliente Cognito e nenhuma sessão injetada.
  const restaurar = !sessaoInicial && (cognitoClient !== undefined || cognitoConfig !== undefined);
  const [estado, setEstado] = useState<AuthState>(() => estadoInicial(sessaoInicial, restaurar));

  // Cliente Cognito estável por toda a vida do provider.
  const clientRef = useRef<CognitoClient | null>(cognitoClient ?? null);
  const getClient = useCallback((): CognitoClient => {
    if (!clientRef.current) {
      if (!cognitoConfig) {
        throw new Error('Configuração do Cognito ausente para o fluxo de login.');
      }
      clientRef.current = new CognitoIdentityClient(cognitoConfig);
    }
    return clientRef.current;
  }, [cognitoConfig]);

  // Resolve o perfil via GET /me com o token recém-emitido. NÃO deriva
  // `mfaVerified` daqui: `me.mfaAtivo` significa "MFA configurado na conta", não
  // "MFA verificado NESTA sessão" (RN-M1-001). O flag de sessão é decidido pelo
  // caminho de login (`aplicarSessao`), não pelo perfil.
  const resolver = useCallback(
    async (token: string): Promise<Omit<AuthUser, 'mfaVerified'>> => {
      if (resolveProfile) {
        const user = await resolveProfile(token);
        return { sub: user.sub, email: user.email, profiles: user.profiles };
      }
      if (!apiBaseUrl) {
        throw new Error('apiBaseUrl ausente para resolver o perfil (GET /me).');
      }
      const meApi = new MeApi(new ApiClient({ baseUrl: apiBaseUrl, getToken: () => token }));
      const me = await meApi.me();
      return {
        sub: me.id,
        email: me.email,
        profiles: [me.perfil],
      };
    },
    [apiBaseUrl, resolveProfile],
  );

  // Limpa a sessão em memória e encerra a sessão Cognito de forma robusta.
  // Ordem: PRIMEIRO limpa o estado (para o 401→logout sempre destravar a
  // sessão, RN-M1-002), DEPOIS aciona o signOut no cliente JÁ existente — sem
  // instanciar um novo cliente (que lançaria quando não há cognitoConfig, p.ex.
  // sessões injetadas via `sessaoInicial`). Rejeição do signOut é engolida para
  // não virar unhandled rejection.
  const limparSessao = useCallback((): void => {
    setEstado({ user: null, loading: false, token: undefined });
    limparMarcaMfa();
    clientRef.current?.signOut().catch(() => undefined);
  }, []);

  const aplicarSessao = useCallback(
    async (token: string, mfaVerified: boolean): Promise<void> => {
      const user = await resolver(token);
      // RN-M1-001 / ADR-052: TODO perfil passa pelo desafio MFA. A sessão que chegou
      // aqui sem MFA verificado (login sem challenge, F5 sem a marca da aba) é
      // inválida — rejeita e não autentica.
      if (!mfaVerified) {
        limparSessao();
        throw new Error('O acesso exige verificação em duas etapas.');
      }
      setEstado({
        user: { ...user, mfaVerified },
        loading: false,
        token,
      });
    },
    [resolver, limparSessao],
  );

  // E-mail do fluxo corrente (rótulo do QR do TOTP); nunca logado.
  const emailRef = useRef<string>('');

  /**
   * Traduz o resultado do Cognito no próximo passo. Uma sessão com o token
   * RESTRITO do primeiro acesso do admin (ADR-021) NÃO vira sessão do sistema:
   * a API nega esse token; o SPA leva ao cadastro do TOTP.
   */
  const proximoPasso = useCallback(
    async (result: SignInResult, mfaVerified: boolean): Promise<ResultadoLogin> => {
      if (isMfaChallenge(result)) {
        return 'mfa';
      }
      if (isNovaSenhaChallenge(result)) {
        return 'nova-senha';
      }
      if (cadastroMfaPendente(result.idToken)) {
        return 'cadastro-mfa';
      }
      await aplicarSessao(tokenParaApi(result), mfaVerified);
      return 'ok';
    },
    [aplicarSessao],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<ResultadoLogin> => {
      emailRef.current = email;
      const result = await getClient().signIn(email, password);
      return proximoPasso(result, false);
    },
    [getClient, proximoPasso],
  );

  const confirmMfa = useCallback(
    async (code: string): Promise<void> => {
      const session = await getClient().confirmMfa(code);
      await aplicarSessao(tokenParaApi(session), true);
      // Só o desafio TOTP grava a marca que o F5 restaura (RN-M1-001, ADR-031).
      marcarMfaVerificado();
    },
    [getClient, aplicarSessao],
  );

  // ADR-031 — boot: restaura a sessão da aba (o SDK renova se o id token venceu) e
  // confere o perfil no GET /me. Sem sessão, token restrito ou falha → tela de login.
  useEffect(() => {
    if (!restaurar) {
      return undefined;
    }
    let ativo = true;
    void (async () => {
      try {
        const sessao = await getClient().refresh();
        if (!ativo) {
          return;
        }
        if (!sessao || cadastroMfaPendente(sessao.idToken)) {
          if (sessao) {
            limparSessao();
          } else {
            setEstado({ user: null, loading: false, token: undefined });
          }
          return;
        }
        await aplicarSessao(tokenParaApi(sessao), mfaVerificadoNaAba());
      } catch {
        if (ativo) {
          limparSessao();
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // Só no boot: as dependências são estáveis por toda a vida do provider.
  }, []);

  // ADR-031 — renovação proativa: antes de o id token vencer, força o refresh; sem
  // refresh válido (8 h vencidas, usuário desativado) → logout.
  const token = estado.token;
  useEffect(() => {
    if (!token || (clientRef.current === null && !cognitoConfig)) {
      return undefined;
    }
    const expira = expiraEmMs(token);
    if (expira === null) {
      return undefined;
    }
    const espera = Math.max(expira - Date.now() - MARGEM_RENOVACAO_MS, ESPERA_MINIMA_MS);
    const timer = setTimeout(() => {
      void getClient()
        .refresh(true)
        .catch(() => null)
        .then((nova) => {
          if (!nova || cadastroMfaPendente(nova.idToken)) {
            limparSessao();
            return;
          }
          setEstado((atual) => (atual.user ? { ...atual, token: tokenParaApi(nova) } : atual));
        });
    }, espera);
    return () => clearTimeout(timer);
  }, [token, cognitoConfig, getClient, limparSessao]);

  const definirNovaSenha = useCallback(
    async (novaSenha: string): Promise<ResultadoLogin> => {
      const result = await getClient().completeNewPassword(novaSenha);
      return proximoPasso(result, false);
    },
    [getClient, proximoPasso],
  );

  const iniciarCadastroTotp = useCallback(
    async (emissor: string): Promise<CadastroTotp> => {
      const segredo = await getClient().associarTotp();
      const conta = emailRef.current || 'usuario';
      const rotulo = `${encodeURIComponent(emissor)}:${encodeURIComponent(conta)}`;
      const uri = `otpauth://totp/${rotulo}?secret=${segredo}&issuer=${encodeURIComponent(emissor)}`;
      return { segredo, uri };
    },
    [getClient],
  );

  const confirmarCadastroTotp = useCallback(
    async (code: string): Promise<void> => {
      await getClient().verificarTotp(code);
      // A sessão restrita não serve para o sistema: encerra e pede novo login
      // (agora com o desafio TOTP e o token com o perfil completo).
      limparSessao();
    },
    [getClient, limparSessao],
  );

  const logout = useCallback((): void => {
    limparSessao();
  }, [limparSessao]);

  const actions = useMemo<AuthActions>(
    () => ({
      login,
      confirmMfa,
      definirNovaSenha,
      iniciarCadastroTotp,
      confirmarCadastroTotp,
      logout,
    }),
    [login, confirmMfa, definirNovaSenha, iniciarCadastroTotp, confirmarCadastroTotp, logout],
  );

  return (
    <AuthContext.Provider value={estado}>
      <AuthActionsContext.Provider value={actions}>{children}</AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}
